import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Button from '@mui/material/Button';
import EditCategoryModal from '../CategorySubCategory/EditCategoryModal';
import CategoryModal from '../CategorySubCategory/CategoryModal';
import AxiosUrl from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiSearch, FiChevronDown, FiChevronRight, FiArrowUp, FiArrowLeft, FiFolder } from 'react-icons/fi';
import categoryStyles from './Category.module.css';
import { AiOutlineClose } from 'react-icons/ai';
import { MdDragIndicator } from 'react-icons/md';

// ── Tree helpers ──────────────────────────────────────────────────────────────

function flattenTree(nodes, depth = 0, result = []) {
    for (const node of nodes) {
        result.push({ ...node, _depth: depth });
        if (node.children && node.children.length > 0) {
            flattenTree(node.children, depth + 1, result);
        }
    }
    return result;
}

function buildVisibleRows(nodes, collapsed, depth = 0, result = []) {
    for (const node of nodes) {
        result.push({ ...node, _depth: depth });
        if (node.children && node.children.length > 0 && !collapsed.has(node._id?.toString())) {
            buildVisibleRows(node.children, collapsed, depth + 1, result);
        }
    }
    return result;
}

// Rebuild a nested tree from a flat array using parentId relationships
function buildTreeFromFlat(nodes) {
    const map = {};
    for (const n of nodes) {
        map[n._id?.toString()] = { ...n, children: [] };
    }
    const roots = [];
    for (const n of nodes) {
        const pid = n.parentId?.toString();
        if (pid && map[pid]) {
            map[pid].children.push(map[n._id?.toString()]);
        } else {
            roots.push(map[n._id?.toString()]);
        }
    }
    function sortLevel(arr) {
        arr.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        for (const n of arr) if (n.children?.length) sortLevel(n.children);
    }
    function assignLevels(arr, lvl) {
        for (const n of arr) {
            n.level = lvl;
            if (n.children?.length) assignLevels(n.children, lvl + 1);
        }
    }
    sortLevel(roots);
    assignLevels(roots, 1);
    return roots;
}

// Detect drop position from mouse Y relative to element
function getDropPos(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientY - rect.top) / rect.height;
    return ratio < 0.25 ? 'before' : ratio > 0.75 ? 'after' : 'child';
}

// ── Constants ────────────────────────────────────────────────────────────────

const LEVEL_COLORS = {
    1: { bg: '#e8f5e9', color: '#2e7d32', label: 'L1' },
    2: { bg: '#e3f2fd', color: '#1565c0', label: 'L2' },
    3: { bg: '#fff3e0', color: '#e65100', label: 'L3' },
    4: { bg: '#fce4ec', color: '#880e4f', label: 'L4' },
};

// ── Component ────────────────────────────────────────────────────────────────

function Category() {
    const uid = localStorage.getItem('id');
    const [tree, setTree] = useState([]);
    const [visibleRows, setVisibleRows] = useState([]);
    const [collapsed, setCollapsed] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimerRef = useRef(null);

    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [editData, setEditData] = useState(null);
    const [refresh, setRefresh] = useState(0);

    // Drag state — ref for values needed during event handlers
    const dragIdRef = useRef(null);
    const [dragOverViz, setDragOverViz] = useState(null); // { id, pos } — visual only
    const [isDragging, setIsDragging] = useState(false);

    const allFlat = useMemo(() => flattenTree(tree), [tree]);

    // ── Data fetching ─────────────────────────────────────────────────────────

    const fetchTree = useCallback(async () => {
        setLoading(true);
        try {
            const res = await AxiosUrl.get(`/category-tree/${uid}`);
            if (res.data.success) {
                const data = res.data.data || [];
                setTree(data);
                // Collapse all nodes that have children by default
                const ids = new Set();
                function collectParents(nodes) {
                    for (const n of nodes) {
                        if (n.children && n.children.length > 0) {
                            ids.add(n._id?.toString());
                            collectParents(n.children);
                        }
                    }
                }
                collectParents(data);
                setCollapsed(ids);
            } else {
                toast.error('Failed to load categories');
            }
        } catch {
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    }, [uid]);

    useEffect(() => {
        if (!openCreate && !openEdit) fetchTree();
    }, [refresh, openCreate, openEdit, fetchTree]);

    // Debounce search input
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 200);
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, [searchQuery]);

    useEffect(() => {
        if (!debouncedSearch) {
            setVisibleRows(buildVisibleRows(tree, collapsed));
        } else {
            const q = debouncedSearch.toLowerCase();
            setVisibleRows(allFlat.filter(r => r.name?.toLowerCase().includes(q)));
        }
    }, [tree, collapsed, debouncedSearch, allFlat]);

    // ── Category actions ──────────────────────────────────────────────────────

    const toggleCollapse = (id) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const deleteCategory = (cid) => {
        if (!window.confirm('Delete this category?')) return;
        AxiosUrl.post(`/delete-category/${uid}`, { cid })
            .then(res => {
                if (res.data.success || res.data.data?.acknowledged || res.data.acknowledged) {
                    toast.success('Deleted');
                    setRefresh(p => p + 1);
                } else {
                    toast.error(res.data.error || 'Failed to delete');
                }
            })
            .catch(() => toast.error('Failed to delete'));
    };

    // ── Drag-and-Drop ─────────────────────────────────────────────────────────

    const clearDragViz = () => {
        dragIdRef.current = null;
        setDragOverViz(null);
        setIsDragging(false);
    };

    const handleDragStart = (e, id) => {
        dragIdRef.current = id;
        setIsDragging(true);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', id);
    };

    const handleDragEnd = () => clearDragViz();

    const handleDragOver = (e, id) => {
        e.preventDefault();
        e.stopPropagation();
        if (!dragIdRef.current || id === dragIdRef.current) return;
        const pos = getDropPos(e);
        setDragOverViz(prev => (prev?.id === id && prev?.pos === pos ? prev : { id, pos }));
    };

    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverViz(null);
        }
    };

    // Root zone drop
    const handleDropRoot = (e) => {
        e.preventDefault();
        const draggedId = dragIdRef.current;
        clearDragViz();
        if (draggedId) applyMove(draggedId, null, 'root');
    };

    // Row drop — compute pos FRESH from event (dragleave may have cleared the ref)
    const handleDrop = (e, targetId) => {
        e.preventDefault();
        e.stopPropagation();
        const draggedId = dragIdRef.current;
        clearDragViz();
        if (!draggedId || draggedId === targetId) return;
        const pos = getDropPos(e); // fresh — no stale ref
        applyMove(draggedId, targetId, pos);
    };

    const applyMove = async (draggedId, targetId, pos) => {
        const dragged = allFlat.find(n => n._id?.toString() === draggedId);
        const target = targetId ? allFlat.find(n => n._id?.toString() === targetId) : null;
        if (!dragged) return;

        // Determine new parentId
        let newParentId;
        if (!targetId || pos === 'root') {
            newParentId = null;
        } else if (pos === 'child') {
            newParentId = targetId;
        } else {
            newParentId = target?.parentId?.toString() || null;
        }

        // Enforce max 4 levels
        if (newParentId) {
            const parentNode = allFlat.find(n => n._id?.toString() === newParentId);
            const parentLevel = parentNode?.level || 1;
            if (parentLevel >= 4) {
                toast.warn('Maximum 4 levels of nesting allowed');
                return;
            }
        }

        const oldParentId = dragged.parentId?.toString() || null;
        if (newParentId === oldParentId && pos !== 'before' && pos !== 'after') {
            // Same parent, same-ish position — skip if already a child
            if (pos === 'child' && newParentId === draggedId) return;
        }

        // Siblings in new parent (excluding dragged)
        const newSiblings = allFlat
            .filter(n => (n.parentId?.toString() || null) === newParentId && n._id?.toString() !== draggedId)
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        // Insert index
        let insertIdx = newSiblings.length;
        if ((pos === 'before' || pos === 'after') && targetId) {
            const tidx = newSiblings.findIndex(n => n._id?.toString() === targetId);
            if (tidx >= 0) insertIdx = pos === 'before' ? tidx : tidx + 1;
        }

        const newOrder = [...newSiblings];
        newOrder.splice(insertIdx, 0, dragged);

        const moves = newOrder.map((item, idx) => ({
            _id: item._id,
            parentId: newParentId,
            sortOrder: idx * 10,
        }));

        // Reorder old parent siblings if parent changed
        if (newParentId !== oldParentId) {
            allFlat
                .filter(n => (n.parentId?.toString() || null) === oldParentId && n._id?.toString() !== draggedId)
                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                .forEach((item, idx) => moves.push({ _id: item._id, parentId: oldParentId, sortOrder: idx * 10 }));
        }

        // ── Optimistic local update (no page reload) ──────────────────────────
        const moveMap = {};
        for (const m of moves) moveMap[m._id?.toString()] = m;

        const updatedFlat = allFlat.map(n => {
            const m = moveMap[n._id?.toString()];
            if (m) return { ...n, parentId: m.parentId, sortOrder: m.sortOrder };
            return n;
        });
        setTree(buildTreeFromFlat(updatedFlat));

        // ── Persist to backend ────────────────────────────────────────────────
        setSaving(true);
        try {
            const res = await AxiosUrl.post(`/move-category/${uid}`, { moves });
            if (!res.data.success) {
                toast.error(res.data.error || 'Failed to save');
                fetchTree(); // Revert to server state on failure
            }
        } catch {
            toast.error('Failed to save');
            fetchTree();
        } finally {
            setSaving(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <>
            <ToastContainer />
            {openCreate && (
                <CategoryModal
                    setOpenCategoryModal={setOpenCreate}
                    open={openCreate}
                    setRefresh={() => setRefresh(p => p + 1)}
                />
            )}
            {openEdit && editData && (
                <EditCategoryModal
                    setOpenModal={setOpenEdit}
                    data={editData}
                    setRefresh={() => setRefresh(p => p + 1)}
                />
            )}

            <div style={{ minHeight: '90vh' }}>
                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>

                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1 }}>
                            <Button
                                variant="contained"
                                onClick={() => setOpenCreate(true)}
                                style={{ backgroundColor: '#008060', color: 'white', padding: '10px 20px', borderRadius: '8px', textTransform: 'none', fontWeight: '500', boxShadow: 'none', fontSize: '14px', whiteSpace: 'nowrap' }}
                            >
                                + Add Category
                            </Button>
                            <div style={{ flex: 1, maxWidth: '360px', display: 'flex', alignItems: 'center', gap: '10px', border: '1px solid #e5e7eb', padding: '8px 14px', borderRadius: '9999px' }}>
                                <input
                                    type="text"
                                    placeholder="Search categories..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '14px', color: '#374151' }}
                                />
                                {searchQuery
                                    ? <AiOutlineClose onClick={() => setSearchQuery('')} style={{ fontSize: '18px', color: '#6b7280', cursor: 'pointer' }} />
                                    : <FiSearch style={{ fontSize: '18px', color: '#6b7280' }} />
                                }
                            </div>
                        </div>
                        <div style={{ fontSize: '13px', color: '#6d7175', fontWeight: '500' }}>
                            {saving ? <span style={{ color: '#008060' }}>Saving...</span> : `${allFlat.length} categories`}
                        </div>
                    </div>


                    {/* Root drop zone */}
                    <div
                        onDragOver={e => e.preventDefault()}
                        onDrop={handleDropRoot}
                        style={{
                            border: isDragging ? '2px dashed #008060' : '2px dashed transparent',
                            borderRadius: '8px',
                            padding: isDragging ? '11px' : '0',
                            maxHeight: isDragging ? '50px' : '0',
                            overflow: 'hidden',
                            marginBottom: isDragging ? '10px' : '0',
                            textAlign: 'center',
                            fontSize: '13px',
                            color: '#008060',
                            fontWeight: '600',
                            background: '#f0fdf4',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        <FiArrowUp style={{ verticalAlign: 'middle' }} /> Drop here to make Root (L1)
                    </div>

                    {/* Table */}
                    <div style={{ border: '1px solid #e1e3e5', borderRadius: '12px', overflow: 'hidden' }}>
                      {/* Horizontal scroll wrapper — keeps right-side columns (Status/Actions) reachable on mobile */}
                      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <div style={{ minWidth: '720px' }}>
                        {/* Head */}
                        <div style={{ display: 'grid', gridTemplateColumns: '28px 52px 1fr 80px 110px 80px 160px', background: '#f8fafc', padding: '11px 14px', gap: '10px', borderBottom: '1px solid #e1e3e5', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
                            <div></div>
                            <div>Image</div>
                            <div>Name</div>
                            <div style={{ textAlign: 'center' }}>Level</div>
                            <div>Parent</div>
                            <div style={{ textAlign: 'center' }}>Status</div>
                            <div style={{ textAlign: 'center' }}>Actions</div>
                        </div>

                        {loading ? (
                            <>
                                {[...Array(8)].map((_, i) => (
                                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px 52px 1fr 80px 110px 80px 160px', padding: '9px 14px', gap: '10px', alignItems: 'center', borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                        <div className={categoryStyles.skeleton} style={{ width: '18px', height: '18px', borderRadius: '4px' }}></div>
                                        <div className={categoryStyles.skeleton} style={{ width: '40px', height: '40px', borderRadius: '6px' }}></div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: `${(i % 3) * 18}px` }}>
                                            <div className={categoryStyles.skeleton} style={{ width: `${120 + (i % 4) * 30}px`, height: '14px' }}></div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <div className={categoryStyles.skeleton} style={{ width: '32px', height: '22px', borderRadius: '10px' }}></div>
                                        </div>
                                        <div className={categoryStyles.skeleton} style={{ width: '70px', height: '14px' }}></div>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <div className={categoryStyles.skeleton} style={{ width: '52px', height: '22px', borderRadius: '10px' }}></div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                            <div className={categoryStyles.skeleton} style={{ width: '48px', height: '28px', borderRadius: '6px' }}></div>
                                            <div className={categoryStyles.skeleton} style={{ width: '56px', height: '28px', borderRadius: '6px' }}></div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        ) : visibleRows.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6d7175' }}>
                                <div style={{ fontSize: '48px', marginBottom: '12px' }}><FiFolder /></div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: '#202223', marginBottom: '6px' }}>
                                    {searchQuery ? 'No categories match' : 'No categories yet'}
                                </div>
                                <div style={{ fontSize: '13px' }}>
                                    {searchQuery ? 'Try a different keyword' : 'Click "+ Add Category" to create one'}
                                </div>
                            </div>
                        ) : (
                            visibleRows.map((row, idx) => {
                                const id = row._id?.toString();
                                const depth = row._depth || 0;
                                const hasChildren = row.children && row.children.length > 0;
                                const level = row.level || (depth + 1);
                                const lc = LEVEL_COLORS[Math.min(level, 4)] || LEVEL_COLORS[1];
                                const isDraggedRow = dragIdRef.current === id;
                                const dViz = dragOverViz?.id === id ? dragOverViz.pos : null;
                                const parentNode = row.parentId
                                    ? allFlat.find(n => n._id?.toString() === row.parentId?.toString())
                                    : null;

                                return (
                                    <div
                                        key={id || idx}
                                        draggable
                                        onDragStart={e => handleDragStart(e, id)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={e => handleDragOver(e, id)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={e => handleDrop(e, id)}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '28px 52px 1fr 80px 110px 80px 160px',
                                            padding: '9px 14px',
                                            gap: '10px',
                                            alignItems: 'center',
                                            userSelect: 'none',
                                            WebkitUserSelect: 'none',
                                            cursor: 'grab',
                                            borderTop: dViz === 'before' ? '2px solid #3b82f6' : '1px solid #f3f4f6',
                                            borderBottom: dViz === 'after' ? '2px solid #3b82f6' : '1px solid #f3f4f6',
                                            background: isDraggedRow ? '#f3f4f6' : dViz === 'child' ? '#d1fae5' : idx % 2 === 0 ? '#fff' : '#fafafa',
                                            opacity: isDraggedRow ? 0.4 : 1,
                                            outline: dViz === 'child' ? '2px dashed #008060' : 'none',
                                            outlineOffset: '-2px',
                                        }}
                                    >
                                        {/* Drag handle */}
                                        <div style={{ color: '#c1c5ca', cursor: 'grab', display: 'flex', alignItems: 'center' }}>
                                            <MdDragIndicator size={18} />
                                        </div>

                                        {/* Image */}
                                        <div style={{ pointerEvents: 'none' }}>
                                            {row.imgUrl
                                                ? <img src={row.imgUrl} alt={row.name} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }} />
                                                : <div style={{ width: 40, height: 40, borderRadius: '6px', background: lc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}><FiFolder /></div>
                                            }
                                        </div>

                                        {/* Name */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', paddingLeft: `${depth * 18}px` }}>
                                            {hasChildren ? (
                                                <button
                                                    onMouseDown={e => e.stopPropagation()}
                                                    onClick={e => { e.stopPropagation(); toggleCollapse(id); }}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#6b7280', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                                >
                                                    {collapsed.has(id) ? <FiChevronRight size={15} /> : <FiChevronDown size={15} />}
                                                </button>
                                            ) : (
                                                <span style={{ width: 19, flexShrink: 0 }} />
                                            )}
                                            {depth > 0 && (
                                                <span style={{ color: '#d1d5db', fontSize: '12px', flexShrink: 0, pointerEvents: 'none' }}>{'— '.repeat(depth)}</span>
                                            )}
                                            <span style={{ fontSize: '13px', fontWeight: depth === 0 ? '600' : '400', color: '#202223', pointerEvents: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {row.name}
                                            </span>
                                            {hasChildren && (
                                                <span style={{ marginLeft: '5px', background: '#f3f4f6', color: '#6b7280', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', fontWeight: '600', flexShrink: 0, pointerEvents: 'none' }}>
                                                    {row.children.length}
                                                </span>
                                            )}
                                            {dViz === 'child' && (
                                                <span style={{ marginLeft: '6px', fontSize: '11px', color: '#008060', fontWeight: '700', flexShrink: 0, pointerEvents: 'none' }}><FiArrowLeft style={{ verticalAlign: 'middle' }} /> nest</span>
                                            )}
                                        </div>

                                        {/* Level */}
                                        <div style={{ textAlign: 'center', pointerEvents: 'none' }}>
                                            <span style={{ background: lc.bg, color: lc.color, padding: '3px 9px', borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>{lc.label}</span>
                                        </div>

                                        {/* Parent */}
                                        <div style={{ fontSize: '12px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                                            {parentNode ? parentNode.name : <span style={{ color: '#d1d5db' }}>Root</span>}
                                        </div>

                                        {/* Status */}
                                        <div style={{ textAlign: 'center', pointerEvents: 'none' }}>
                                            <span style={{
                                                background: row.status === 1 ? '#fef2f2' : '#f0fdf4',
                                                color: row.status === 1 ? '#991b1b' : '#166534',
                                                border: `1px solid ${row.status === 1 ? '#fecaca' : '#bbf7d0'}`,
                                                padding: '3px 9px', borderRadius: '10px', fontSize: '12px', fontWeight: '500'
                                            }}>
                                                {row.status === 1 ? 'Inactive' : 'Active'}
                                            </span>
                                        </div>

                                        {/* Actions */}
                                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                            <Button
                                                size="small"
                                                onMouseDown={e => e.stopPropagation()}
                                                onClick={e => { e.stopPropagation(); setEditData(row); setOpenEdit(true); }}
                                                style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', fontSize: '12px', padding: '4px 11px', borderRadius: '6px', textTransform: 'none', boxShadow: 'none', minWidth: 0 }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="small"
                                                onMouseDown={e => e.stopPropagation()}
                                                onClick={e => { e.stopPropagation(); deleteCategory(row._id); }}
                                                style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', fontSize: '12px', padding: '4px 11px', borderRadius: '6px', textTransform: 'none', boxShadow: 'none', minWidth: 0 }}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        </div>
                      </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Category;
